import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { useLocale } from "../i18n/LocaleContext";
import { CATEGORIES } from "../data/treatments";
import { WHATSAPP_URL } from "../config";
import { WhatsAppIcon } from "./icons";
import { LanguageSwitcher } from "./LanguageSwitcher";

const NAV_KEYS = ["home", "treatments", "about", "studio", "contact"];

export function Header() {
  const { t, link, lang } = useLocale();
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileTreatmentsOpen, setMobileTreatmentsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const navLink = (key) => (
    <Link
      key={key}
      to={link(key)}
      data-testid={`nav-${key}`}
      className="link-line text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown transition-colors hover:text-imperium-gold"
    >
      {t(`nav.${key}`)}
    </Link>
  );

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "border-b border-imperium-champagne/30 bg-imperium-white/95 backdrop-blur-md" : "bg-transparent"}`}
      >
        <div className="relative mx-auto flex h-20 max-w-[1600px] items-center justify-between px-6 sm:px-10 lg:px-16">
          <button
            type="button"
            data-testid="mobile-menu-button"
            aria-label={mobileOpen ? t("header.closeMenu") : t("header.openMenu")}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
            className="z-10 -ml-1 p-1 text-imperium-brown lg:hidden"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <Link
            to={link("home")}
            data-testid="header-logo-link"
            aria-label="IMPERIUM Beauty & Wellness"
            onClick={() => setMobileOpen(false)}
            className="max-lg:absolute max-lg:left-1/2 max-lg:-translate-x-1/2"
          >
            <img src="/assets/img/logo-header.png" alt="IMPERIUM Beauty & Wellness" className="h-14 w-auto lg:h-16" />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Main">
            {navLink("home")}
            <div className="relative" onMouseEnter={() => setDropOpen(true)} onMouseLeave={() => setDropOpen(false)}>
              <button
                type="button"
                data-testid="nav-treatments-button"
                aria-expanded={dropOpen}
                onClick={() => setDropOpen((o) => !o)}
                className="flex items-center gap-1.5 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown transition-colors hover:text-imperium-gold"
              >
                {t("nav.treatments")}
                <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${dropOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {dropOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="treatments-dropdown"
                    className="absolute left-1/2 top-full w-[560px] -translate-x-1/2 pt-6"
                  >
                    <div className="grid grid-cols-1 border border-imperium-champagne/40 bg-imperium-white shadow-[0_30px_80px_-30px_rgba(48,37,31,0.4)]">
                      {CATEGORIES.map((cat, i) => (
                        <Link
                          key={cat.key}
                          to={link("treatments", cat.slugs[lang])}
                          data-testid={`dropdown-category-${cat.key}`}
                          onClick={() => setDropOpen(false)}
                          className="group flex items-baseline justify-between gap-4 border-b border-imperium-cream px-6 py-4 transition-colors last:border-b-0 hover:bg-imperium-offwhite"
                        >
                          <span className="flex items-baseline gap-3">
                            <span className="text-[10px] font-label text-imperium-champagne">{String(i + 1).padStart(2, "0")}</span>
                            <span className="font-serif text-lg leading-snug text-imperium-black transition-colors group-hover:text-imperium-bronze">{cat.names[lang]}</span>
                          </span>
                          <ArrowUpRight className="h-3.5 w-3.5 shrink-0 text-imperium-champagne transition-all group-hover:text-imperium-gold" />
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {navLink("about")}
            {navLink("studio")}
            {navLink("contact")}
          </nav>

          <div className="hidden items-center gap-8 lg:flex">
            <LanguageSwitcher />
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-cta"
              className="flex items-center gap-2.5 border border-imperium-bronze px-6 py-3 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown transition-colors duration-300 hover:bg-imperium-brown hover:text-imperium-white"
            >
              <WhatsAppIcon className="h-4 w-4 text-[#25D366]" />
              WhatsApp
            </a>
          </div>

          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-header-whatsapp"
            aria-label="WhatsApp"
            className="flex h-10 w-10 items-center justify-center text-[#25D366] lg:hidden"
          >
            <WhatsAppIcon className="h-7 w-7" />
          </a>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-imperium-white pt-28 lg:hidden"
          >
            <nav className="flex flex-col px-8 pb-16" aria-label="Mobile">
              {[
                { key: "home", to: link("home") },
                { key: "treatments" },
                { key: "about", to: link("about") },
                { key: "studio", to: link("studio") },
                { key: "contact", to: link("contact") },
              ].map((item, idx) =>
                item.key === "treatments" ? (
                  <div key="treatments" className="border-b border-imperium-cream">
                    <button
                      type="button"
                      data-testid="mobile-treatments-toggle"
                      aria-expanded={mobileTreatmentsOpen}
                      onClick={() => setMobileTreatmentsOpen((o) => !o)}
                      className="flex w-full items-center justify-between py-5 text-left"
                    >
                      <span className="font-serif text-3xl text-imperium-black">{t("nav.treatments")}</span>
                      <ChevronDown className={`h-5 w-5 text-imperium-bronze transition-transform duration-300 ${mobileTreatmentsOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {mobileTreatmentsOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-col pb-6 pl-1">
                            <Link to={link("treatments")} data-testid="mobile-treatments-all" onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-label uppercase tracking-[0.2em] text-imperium-bronze">
                              {t("cta.allTreatments")}
                            </Link>
                            {CATEGORIES.map((cat) => (
                              <Link
                                key={cat.key}
                                to={link("treatments", cat.slugs[lang])}
                                data-testid={`mobile-category-${cat.key}`}
                                onClick={() => setMobileOpen(false)}
                                className="py-2.5 font-serif text-xl text-imperium-black/80"
                              >
                                {cat.names[lang]}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div key={item.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 * idx, duration: 0.5 }}>
                    <Link
                      to={item.to}
                      data-testid={`mobile-nav-${item.key}`}
                      onClick={() => setMobileOpen(false)}
                      className="block border-b border-imperium-cream py-5 font-serif text-3xl text-imperium-black"
                    >
                      {t(`nav.${item.key}`)}
                    </Link>
                  </motion.div>
                )
              )}
              <div className="mt-10 flex flex-col gap-8">
                <LanguageSwitcher />
                <Link
                  to={link("contact")}
                  data-testid="mobile-booking-cta"
                  onClick={() => setMobileOpen(false)}
                  className="inline-flex w-fit border border-imperium-bronze px-8 py-4 text-[11px] font-label uppercase tracking-[0.25em] text-imperium-brown"
                >
                  {t("cta.book")}
                </Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
